import {
  DataTableDataSearchDto,
  DetailPageWithoutFilterDto,
  ManualDefinitionTableDataValue,
  TableBulkDetailPageActionConfigDto,
  TableBulkGeneralActionConfigDto,
  TableBulkWorkflowActionConfigDto,
  TableCacheKeyDto,
  TableDataDto,
  TableRowDto,
  TableServiceRowBulkActionConfigDto,
} from '@asset-metrix/portal-gateway-client-js';
import { WidgetMoreAction } from '@asset-metrix/portal-ui';
import type { TFunction } from 'i18next';
import { z } from 'zod';

import { datatableDeleteRows } from '../../api/portalGateway/datatableDeleteRows';
import { datatableDownloadDocuments } from '../../api/portalGateway/datatableDownloadDocuments';
import { datatableMarkAsRead } from '../../api/portalGateway/datatableMarkAsRead';
import { downloadTableRow } from '../../api/portalGateway/downloadTableRow';
import { executeTableRowRequest } from '../../api/portalGateway/executeTableRowRequest';
import { actionWithAsyncNotification } from '../../utils/actionWithAsyncNotification';
import { extractErrorMessage } from '../../utils/errorMessage';
import {
  invalidateAllTableCaches,
  invalidateTable,
} from '../../utils/queryCacheHelper';

// For some actions, the changes are not immediately visible after the action
// has been executed.
// For these cases, the cache invalidation has to be deferred for a short time,
// so the updates is not lost.
// This deferrer will only be added for actions if needed, e.g. for the bulk
// delete
const INVALIDATION_DEFER_TIME_MS = 500;

const appendLabelSuffix = (label: string, rowCount: number) =>
  label + `${rowCount > 0 ? ` (${rowCount})` : ''}`;

const getLabel = (
  t: TFunction,
  actionKey: TableBulkGeneralActionConfigDto['actionKey'],
  rowCount: number
) =>
  appendLabelSuffix(
    t(`app.widget.action.label.${actionKey}` as const),
    rowCount
  );

export const getId = (
  row: TableDataDto['items'][number]
): ManualDefinitionTableDataValue => row.actionRowId ?? row.id;

/** Row id as a non-empty string, or undefined when the row has no usable id. */
export function getRowId(
  row: TableDataDto['items'][number]
): string | undefined {
  const raw = getId(row);
  if (typeof raw !== 'string' && typeof raw !== 'number') {
    return undefined;
  }
  const id = String(raw);
  return id === '' ? undefined : id;
}

export function widgetBulkAction(
  widgetId: string,
  widgetTitle: string,
  action:
    | TableBulkGeneralActionConfigDto
    | TableBulkWorkflowActionConfigDto
    | TableBulkDetailPageActionConfigDto
    | TableServiceRowBulkActionConfigDto,
  cacheKey: TableCacheKeyDto,
  rows: TableDataDto['items'],
  pageFilterSelections: DataTableDataSearchDto['selection'],
  t: TFunction,
  openDetailPage: (
    detailPageConfig: DetailPageWithoutFilterDto,
    dataRows: Record<string, unknown>[]
  ) => void,
  openDeleteConfirmation: (
    rowIdsToBeDeleted: string[],
    onApply: () => void
  ) => void,
  workflowActionHandler?: (
    modalId: string,
    rowIds: string[],
    action: string,
    data: TableRowDto['data'][]
  ) => void
): WidgetMoreAction {
  let rowsToUpdate: TableDataDto['items'] = [];
  switch (action.actionKey) {
    case 'markAsRead':
      rowsToUpdate = rows.filter(
        (row) => row.readByCurrentUser === false && getId(row) != null
      );
      return {
        disabled: rowsToUpdate.length === 0,
        label: getLabel(t, action.actionKey, rowsToUpdate.length),
        cb: async () => {
          actionWithAsyncNotification(
            () =>
              datatableMarkAsRead({
                widgetId,
                data: {
                  isRead: true,
                  documentIds: rowsToUpdate.map((row) => getId(row) as string),
                },
              }),
            {
              successMessage: t('app.notifications.markAsRead.success', {
                count: rowsToUpdate.length,
              }),
              errorMessage: t('app.notifications.markAsRead.error', {
                count: rowsToUpdate.length,
              }),
              onSuccess: async () => {
                await invalidateTable(cacheKey);
              },
            }
          );
        },
      };
    case 'markAsUnread':
      rowsToUpdate = rows.filter(
        (row) => row.readByCurrentUser === true && getId(row) != null
      );
      return {
        disabled: rowsToUpdate.length === 0,
        label: getLabel(t, action.actionKey, rowsToUpdate.length),
        cb: async () => {
          actionWithAsyncNotification(
            () =>
              datatableMarkAsRead({
                widgetId,
                data: {
                  isRead: false,
                  documentIds: rowsToUpdate.map((row) => getId(row) as string),
                },
              }),
            {
              successMessage: t('app.notifications.markAsUnread.success', {
                count: rowsToUpdate.length,
              }),
              errorMessage: t('app.notifications.markAsUnread.error', {
                count: rowsToUpdate.length,
              }),
              onSuccess: async () => {
                await invalidateTable(cacheKey);
              },
            }
          );
        },
      };
    case 'download':
      rowsToUpdate = rows.filter(
        (row) => row.virus_free !== false && row.actionRowId != null
      );
      return {
        disabled: rowsToUpdate.length === 0,
        label: getLabel(t, action.actionKey, rowsToUpdate.length),
        cb: async () => {
          actionWithAsyncNotification(
            () =>
              datatableDownloadDocuments({
                widgetId,
                data: {
                  actionRowIds: rowsToUpdate.map((row) =>
                    String(row.actionRowId)
                  ),
                },
                fileName: widgetTitle || `documents-${widgetId}`,
              }),
            {
              startMessage: t('app.notifications.bulkDownload.loading', {
                count: rowsToUpdate.length,
              }),
              successMessage: t('app.notifications.bulkDownload.success', {
                count: rowsToUpdate.length,
              }),
              errorMessage: t('app.notifications.bulkDownload.error', {
                count: rowsToUpdate.length,
              }),
              showBackLink: true,
              onSuccess: async () => {
                if (
                  rowsToUpdate.some((row) => row.readByCurrentUser === false)
                ) {
                  await invalidateTable(cacheKey);
                }
              },
            }
          );
        },
      };
    case 'download-service-row-action':
      rowsToUpdate = rows.filter((row) => row.actionRowId != null);
      return {
        disabled:
          rowsToUpdate.length === 0 ||
          (action.singleSelectionOnly && rows.length > 1),
        label: appendLabelSuffix(action.label, rowsToUpdate.length),
        cb: async () => {
          actionWithAsyncNotification(
            () =>
              downloadTableRow({
                contentId: widgetId,
                actionRowIds: rowsToUpdate.map((row) =>
                  String(row.actionRowId)
                ),
                actionId: action.actionId,
                defaultFileName: action.label,
                ...(pageFilterSelections != null &&
                  Object.keys(pageFilterSelections).length > 0 && {
                    context: { pageFilterSelections },
                  }),
              }),
            {
              startMessage: t('app.notifications.downloadRow.loading', {
                count: rowsToUpdate.length,
              }),
              successMessage: t('app.notifications.downloadRow.success', {
                count: rowsToUpdate.length,
              }),
              errorMessage: (error) =>
                t('app.notifications.downloadRow.error', {
                  count: rowsToUpdate.length,
                  message: extractErrorMessage(error),
                }),
              showBackLink: true,
            }
          );
        },
      };
    case 'request-service-row-action':
      rowsToUpdate = rows.filter((row) => row.actionRowId != null);
      return {
        disabled:
          rowsToUpdate.length === 0 ||
          (action.singleSelectionOnly && rows.length > 1),
        label: appendLabelSuffix(action.label, rowsToUpdate.length),
        cb: async () => {
          actionWithAsyncNotification(
            () =>
              executeTableRowRequest({
                widgetId,
                data: {
                  actionRowIds: rowsToUpdate.map((row) =>
                    String(row.actionRowId)
                  ),
                  actionId: action.actionId,
                  ...(pageFilterSelections != null &&
                    Object.keys(pageFilterSelections).length > 0 && {
                      context: {
                        pageFilterSelections,
                      },
                    }),
                },
              }),
            {
              startMessage: t('app.notifications.rowRequest.loading', {
                count: rowsToUpdate.length,
                label: action.label,
              }),
              successMessage: t('app.notifications.rowRequest.success', {
                count: rowsToUpdate.length,
                label: action.label,
              }),
              errorMessage: (error) =>
                t('app.notifications.rowRequest.error', {
                  count: rowsToUpdate.length,
                  label: action.label,
                  message: extractErrorMessage(error),
                }),
              showBackLink: false,
              onFinished: () => {
                invalidateAllTableCaches(cacheKey);
              },
            }
          );
        },
      };
    case 'workflow': {
      const affectedRows = rows
        .filter((row): row is Row => rowSchema.safeParse(row).success)
        .filter((row) =>
          row.workflowActions.actions
            .filter((action) => !action.disabled)
            .map((action) => action.key)
            .includes(action.workflowOptions.key)
        );
      const rowCount = affectedRows.length;
      return {
        disabled: rowCount === 0,
        label:
          action.workflowOptions.label +
          `${rowCount > 0 ? ` (${rowCount})` : ''}`,
        cb: () => {
          const rowIds = affectedRows.map((row) =>
            String(row.workflowActions.rowId)
          );
          const { modalId, key } = action.workflowOptions;
          workflowActionHandler?.(modalId, rowIds, key, affectedRows);
        },
      };
    }
    case 'delete':
      rowsToUpdate = rows.filter((row) => getId(row) != null);

      return {
        disabled: rowsToUpdate.length === 0,
        label: getLabel(t, action.actionKey, rowsToUpdate.length),
        cb: async () => {
          const rowIds = rowsToUpdate.map((row) => getId(row) as string);
          openDeleteConfirmation(rowIds, async () => {
            await actionWithAsyncNotification(
              () =>
                datatableDeleteRows({
                  widgetId,
                  data: {
                    rowIds,
                    ...(pageFilterSelections != null &&
                      Object.keys(pageFilterSelections).length > 0 && {
                        context: {
                          pageFilterSelections,
                        },
                      }),
                  },
                }),
              {
                successMessage: t('app.notifications.delete.success', {
                  count: rowsToUpdate.length,
                }),
                errorMessage: t('app.notifications.delete.error', {
                  count: rowsToUpdate.length,
                }),
                onSuccess: async () => {
                  setTimeout(async () => {
                    await invalidateAllTableCaches(cacheKey);
                  }, INVALIDATION_DEFER_TIME_MS);
                },
              }
            );
          });
        },
      };
    case 'openDetailPage':
      return {
        disabled: rows.length === 0,
        label: `${t('app.widget.action.label.openDetailPage', '', { label: action.label })}${rows.length > 0 ? ` (${rows.length})` : ''}`,
        cb: () => {
          openDetailPage(action.detailPageConfig, rows);
        },
      };
  }
}

const rowSchema = z.object({
  workflowActions: z.object({
    actions: z.array(z.object({ key: z.string(), disabled: z.boolean() })),
    rowId: z.union([z.number(), z.string()]),
  }),
});

type Row = z.infer<typeof rowSchema>;
